import { labelVariants } from '@heroui/react';
import { ExtraParamsKey, experience, SignInIdentifier } from '@logto/schemas';
import { useId } from 'react';

import { backStepState } from '@/Layout/AppLayout/use-step-transition';
import TextLink from '@/components/TextLink';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import DynamicT from '@/shared/components/DynamicT';
import { UserFlow } from '@/types';
import { formatPhoneNumberWithCountryCallingCode } from '@/utils/country-code';
import { identifierInputPlaceholderMap } from '@/utils/form';

type Props = {
  readonly identifier: SignInIdentifier;
  readonly value: string;
};

/** The identifier from the previous step, read-only, with a way back to change it. */
const IdentifierSummary = ({ identifier, value }: Props) => {
  const navigate = useNavigateWithPreservedSearchParams();
  const valueId = useId();

  return (
    <div className="flex flex-col gap-1">
      <span className={labelVariants()}>
        <DynamicT forKey={identifierInputPlaceholderMap[identifier]} />
      </span>
      <div className="flex min-h-11 items-center gap-3 rounded-field bg-default ps-5 pe-4 md:min-h-10">
        <bdi
          id={valueId}
          className="min-w-0 flex-1 py-2 text-base text-foreground wrap-anywhere sm:text-sm"
        >
          {identifier === SignInIdentifier.Phone
            ? formatPhoneNumberWithCountryCallingCode(value)
            : value}
        </bdi>
        <TextLink
          className="shrink-0"
          text="action.edit"
          aria-describedby={valueId}
          onClick={() => {
            // Phone has its own identifier screen; the main one may not offer it inline.
            navigate(
              identifier === SignInIdentifier.Phone
                ? {
                    pathname: `/${experience.routes.identifierSignIn}`,
                    search: new URLSearchParams({
                      [ExtraParamsKey.Identifier]: SignInIdentifier.Phone,
                    }).toString(),
                  }
                : `/${UserFlow.SignIn}`,
              { state: backStepState }
            );
          }}
        />
      </div>
    </div>
  );
};

export default IdentifierSummary;
